import React, { useState, useEffect, useMemo } from 'react';
import {dateMillis} from '../utils/user-directory';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { FirebaseUser } from '../types/user';
import './stylesheets/QuestionBankAnalysis.css';
import SelectedUsersReadQuestions from '../components/SelectedUsersReadQuestions';

interface QuestionReadData {
  uid: string;
  readQuestions: string[];
  lastReadAt?: string;
}

const QuestionBankAnalysis: React.FC = () => {
  const [questionReadData, setQuestionReadData] = useState<QuestionReadData[]>([]);
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ 
    key: 'userName', 
    direction: 'asc' 
  });
  
  // Filter state
  type ReadFilter = 'all' | 'readToday' | 'readLast7Days' | 'activeReaders';
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [selectedUserData, setSelectedUserData] = useState<QuestionReadData | null>(null);
  const [hideTestUsers, setHideTestUsers] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load question read data
      const questionReadCollection = collection(db, 'questionBankQuestionsRead');
      const questionReadSnapshot = await getDocs(questionReadCollection);
      const questionReadList = questionReadSnapshot.docs.map(doc => ({
        uid: doc.id,
        readQuestions: doc.data().readQuestions || [],
        lastReadAt: doc.data().lastReadAt || null
      })) as QuestionReadData[];
      
      // Load users
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as FirebaseUser[];
      
      setQuestionReadData(questionReadList);
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse date strings
  const parseDate = (dateString: string): Date => {
    return new Date(dateString);
  };

  // Helper function to get user name from UID
  const getUserName = (uid: string): string => {
    const user = users.find(u => u.uid === uid);
    return user?.name || user?.email || 'Unknown User';
  };

  // Helper function to get user email from UID
  const getUserEmail = (uid: string): string => {
    const user = users.find(u => u.uid === uid);
    return user?.email || 'N/A';
  };

  // Helper function to get user creation date
  const getUserCreatedAt = (uid: string): Date | null => {
    const user = users.find(u => u.uid === uid);
    const millis=dateMillis(user?.createdAt);
    return millis===null?null:new Date(millis);
  };

  // Table sorting and filtering
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...questionReadData];
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Filter out test users if hideTestUsers is enabled
    if (hideTestUsers) {
      filtered = filtered.filter(data => {
        const userName = getUserName(data.uid);
        return userName !== 'Mock Medic Admin' && userName !== 'Test User 3';
      });
    }

    // Apply analytics card filter
    if (readFilter === 'readToday') {
      filtered = filtered.filter(data => {
        if (!data.lastReadAt) return false;
        const lastRead = parseDate(data.lastReadAt);
        return lastRead >= todayStart && lastRead <= now;
      });
    } else if (readFilter === 'readLast7Days') {
      filtered = filtered.filter(data => {
        if (!data.lastReadAt) return false;
        const lastRead = parseDate(data.lastReadAt);
        return lastRead >= sevenDaysAgo && lastRead <= now;
      });
    } else if (readFilter === 'activeReaders') {
      filtered = filtered.filter(data => data.readQuestions.length > 0);
    }

    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(data => {
        const userName = getUserName(data.uid);
        const userEmail = getUserEmail(data.uid);
        const questionsCount = String(data.readQuestions.length);
        return [userName, userEmail, questionsCount, data.uid]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(term));
      });
    }

    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;

      let aVal: any;
      let bVal: any;

      switch (key) {
        case 'userName':
          aVal = getUserName(a.uid);
          bVal = getUserName(b.uid);
          break;
        case 'questionsRead':
          aVal = a.readQuestions.length;
          bVal = b.readQuestions.length;
          break;
        case 'lastReadAt':
          aVal = a.lastReadAt ? parseDate(a.lastReadAt).getTime() : 0;
          bVal = b.lastReadAt ? parseDate(b.lastReadAt).getTime() : 0;
          break;
        case 'createdAt':
          const aDate = getUserCreatedAt(a.uid);
          const bDate = getUserCreatedAt(b.uid);
          aVal = aDate ? aDate.getTime() : 0;
          bVal = bDate ? bDate.getTime() : 0;
          break;
        default:
          aVal = (a as any)[key];
          bVal = (b as any)[key];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      return (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) * dir;
    });

    return sorted;
  }, [questionReadData, users, search, sortConfig, readFilter, hideTestUsers]);

  const toggleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'questionsRead' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Calculate analytics
  const calculateAnalytics = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Filter out test users if hideTestUsers is enabled
    let filteredData = [...questionReadData];
    if (hideTestUsers) {
      filteredData = filteredData.filter(data => {
        const userName = getUserName(data.uid);
        return userName !== 'Mock Medic Admin' && userName !== 'Test User 3';
      });
    }

    // 1. Total users who have read questions
    const totalActiveReaders = filteredData.filter(data => data.readQuestions.length > 0).length;

    // 4. Total questions read across all users
    const totalQuestionsRead = filteredData.reduce((total, data) => 
      total + data.readQuestions.length, 0
    );

    // 5. Average questions read per active user
    const avgQuestionsPerUser = totalActiveReaders > 0 ? 
      Math.round(totalQuestionsRead / totalActiveReaders) : 0;

    return {
      totalActiveReaders,
      totalQuestionsRead,
      avgQuestionsPerUser
    };
  };

  const analytics = useMemo(() => calculateAnalytics(), [questionReadData, users, hideTestUsers]);

  // Helper function to get selected user for modal
  const getSelectedUser = (): FirebaseUser | null => {
    if (!selectedUserData) return null;
    return users.find(u => u.uid === selectedUserData.uid) || null;
  };

  return (
    <div className="analysis-page" style={selectedUserData ? {overflow: 'hidden', height: '100vh'} : {}}>
      <h2>Question Bank Analysis</h2>
      
      {/* Analytics Cards */}
      <div className="analytics-grid" style={{marginBottom: '30px'}}>
        <div
          className={`analytics-card${readFilter==='activeReaders' ? ' active' : ''}`}
          style={{cursor:'pointer', border: readFilter==='activeReaders' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setReadFilter('activeReaders')}
        >
          <h3>Active Readers</h3>
          <div className="analytics-number">{analytics.totalActiveReaders}</div>
        </div>
        <div className="analytics-card">
          <h3>Total Questions Read</h3>
          <div className="analytics-number">{analytics.totalQuestionsRead}</div>
        </div>
        <div className="analytics-card">
          <h3>Avg Questions/Reader</h3>
          <div className="analytics-number">{analytics.avgQuestionsPerUser}</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex', 
        justifyContent: 'flex-end', 
        marginBottom: '20px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setHideTestUsers(!hideTestUsers)}
          style={{
            background: hideTestUsers ? '#ff9800' : '#2196f3',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hideTestUsers ? '#f57c00' : '#1976d2';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = hideTestUsers ? '#ff9800' : '#2196f3';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <span>{hideTestUsers ? '👁️' : '🚫'}</span>
          {hideTestUsers ? 'Show Test Users' : 'Hide Test Users'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading question bank data...</div>
      ) : (
        <div className="attempts-table-wrapper">
          <div style={{marginBottom: '16px', display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'center'}}>
            <input
              type="text"
              placeholder="Search by user name, email, or questions read..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: 6,
                border: '5px solid #949494ff',
                background: '#585757ff',
                color: '#f3f3f3ff',
                flex: '1 1 260px'
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background:'#444',
                  color:'#eee',
                  border:'1px solid #555',
                  padding:'10px 16px',
                  borderRadius:6,
                  cursor:'pointer'
                }}
              >Clear</button>
            )}
            <span style={{marginLeft:'auto', color:'#64b5f6', fontWeight:600, fontSize:'16px', paddingRight:'18px'}}>
              {filteredAndSortedData.length} result{filteredAndSortedData.length === 1 ? '' : 's'}
            </span>
          </div>
          
          <div className="attempts-table-container">
            <table className="attempts-table">
              <thead>
                <tr>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('userName')}>
                    User Name{sortIndicator('userName')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('uid')}>
                    User ID{sortIndicator('uid')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('questionsRead')}>
                    Questions Read{sortIndicator('questionsRead')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('createdAt')}>
                    User Created{sortIndicator('createdAt')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedData.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="no-data">
                      No question bank data found.
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedData.map((data) => (
                    <tr key={data.uid} style={{cursor:'pointer'}} onClick={() => setSelectedUserData(data)}>
                      <td data-label="User Name">{getUserName(data.uid)}</td>
                      <td data-label="User ID">
                        <span style={{
                          fontFamily: 'monospace', 
                          fontSize: '12px',
                          wordBreak: 'break-all',
                          lineHeight: '1.2'
                        }}>
                          {data.uid}
                        </span>
                      </td>
                      <td data-label="Questions Read">
                        <span style={{fontWeight: '600', color: '#64b5f6'}}>
                          {data.readQuestions.length}
                        </span>
                      </td>
                      <td data-label="User Created">
                        {(() => {
                          const createdAt = getUserCreatedAt(data.uid);
                          return createdAt ? (
                            <span style={{display:'flex', flexDirection:'column'}}>
                              <span>{createdAt.toLocaleDateString()}</span>
                              <span style={{color: '#b0b0b0', fontSize: '13px'}}>
                                {createdAt.toLocaleTimeString()}
                              </span>
                            </span>
                          ) : (
                            <span style={{color: '#888', fontStyle: 'italic'}}>Unknown</span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Reading Details Modal */}
      {selectedUserData && (
        <SelectedUsersReadQuestions
          userData={selectedUserData}
          user={getSelectedUser()}
          onClose={() => setSelectedUserData(null)}
        />
      )}
    </div>
  );
};

export default QuestionBankAnalysis;
